import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';

export type EmptyStateSize = 'sm' | 'md' | 'lg';
export type EmptyStateVariant = 'plain' | 'box';

export interface EmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: IconKind | null;
  title: React.ReactNode;
  sub?: React.ReactNode;
  action?: React.ReactNode;
  size?: EmptyStateSize;
  /** `box` renders a dashed, rounded container — use inside tables, lists and cards. */
  variant?: EmptyStateVariant;
  /** Stretch to fill the parent and center vertically (for split panes / full-height lists). */
  fill?: boolean;
}

const SIZE = {
  sm: {
    root: 'px-[var(--sp-5)] py-[var(--sp-8)]',
    tile: 'mb-[var(--sp-3)] h-10 w-10 rounded-[var(--r-md)]',
    icon: 18,
    title: 'text-[14px]',
    sub: 'mt-[var(--sp-1)] text-[12.5px]',
    action: 'mt-[var(--sp-4)]',
  },
  md: {
    root: 'px-[var(--sp-6)] py-[var(--sp-10)]',
    tile: 'mb-[18px] h-[60px] w-[60px] rounded-2xl',
    icon: 28,
    title: 'text-[19px]',
    sub: 'mt-1.5 text-[13.5px]',
    action: 'mt-[var(--sp-5)]',
  },
  lg: {
    root: 'px-[var(--sp-8)] py-[var(--sp-16)]',
    tile: 'mb-[var(--sp-5)] h-[72px] w-[72px] rounded-[var(--r-xl)]',
    icon: 32,
    title: 'text-[22px]',
    sub: 'mt-[var(--sp-2)] text-[14px]',
    action: 'mt-[var(--sp-6)]',
  },
} as const satisfies Record<EmptyStateSize, Record<string, string | number>>;

export function EmptyState({
  icon = 'folder',
  title,
  sub,
  action,
  size = 'md',
  variant = 'plain',
  fill = false,
  className,
  ...rest
}: EmptyStateProps) {
  const s = SIZE[size];
  return (
    <div
      role="status"
      className={cn(
        'flex flex-col items-center text-center',
        s.root,
        variant === 'box' &&
          'rounded-[var(--r-lg)] border border-dashed border-[var(--cream-2)] bg-[var(--cream-0)]',
        fill && 'min-h-0 flex-1 justify-center',
        className,
      )}
      {...rest}
    >
      {icon ? (
        <div
          className={cn(
            'flex items-center justify-center border border-[var(--cream-2)] text-[var(--ink-3)]',
            variant === 'box' ? 'bg-[var(--cream-1)]' : 'bg-[var(--cream-0)]',
            s.tile,
          )}
        >
          <Icon kind={icon} size={s.icon} stroke={1.5} />
        </div>
      ) : null}
      <div className={cn('font-[var(--font-display)] font-semibold text-[var(--ink-0)]', s.title)}>
        {title}
      </div>
      {sub ? <div className={cn('max-w-[34ch] leading-normal text-[var(--ink-3)]', s.sub)}>{sub}</div> : null}
      {action ? <div className={s.action}>{action}</div> : null}
    </div>
  );
}
